import { COL_DISPLAY_ORDER } from './../../exogenous-lines/exogenous.spread.config';
import { PermissionsService } from '@services/auth/permissions.service';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
  HostListener,
} from '@angular/core';
import * as GC from '@grapecity/spread-sheets';
import { clone, isEmpty, isNumber } from '@helpers/arrays';
import { getDateFromString } from '@helpers/getDateFromString';
import { mapActionKeys, bindSpreadJSEventsToSheet } from '@helpers/spread/map-action-keys';
import { checkDropdownValues } from '@helpers/spread/sheet-functions';
import * as SheetFunctions from '@helpers/spread/sheet-functions';
import { selectRow } from '@helpers/spread/sheet-selection';
import { getColumnNames } from '@helpers/spread/sheet-functions';
import { BalanceSheet, BalanceSheetDB } from '@models/balance-sheet';
import {
  BalanceSheetCategory,
  NON_MATURITY_DEPOSIT_INDEX,
  AUXILIARY_ASSET_INDEX,
  AUXILIARY_LIABILITY_INDEX,
} from '@models/balance-sheet-category';
import { DialogSaveObject } from '@models/dialog-save-object';
import { RetrievedBalanceSheet } from '@models/retrieved-balance-sheet';
import { UserEditTrackingItem } from '@models/user-edit-tracking-item';
import { OpenIdConnectService } from '@services/auth/open-id-connect.service';
import { BalanceSheetService } from '@services/balance-sheet/balance-sheet.service';
import { MenuService } from '@services/menu/menu.service';
import { NotificationsService } from '@services/notifications/notifications.service';
import {Toast} from 'ngx-toastr';
import { setCategoryStyle, setNonCategoryStyle } from '@shared/helpers/spread/cell-styling';
import { setDefaultBookOptions, setDefaultSheetOptions } from '@shared/spreadsheet/base';
import { SpreadContextMenu } from '@shared/spreadsheet/protection';
import { EventEmitter } from 'events';
import { BehaviorSubject, forkJoin, fromEvent, Observable, of, Subject } from 'rxjs';
import { throttleTime, distinctUntilChanged, skip, takeUntil } from 'rxjs/operators';
import { spreadsheetConfig } from '@shared/spreadsheet/spreadsheet-config';
import {
  BALANCE_SHEET_CHECK_RATE,
  DEBOUNCE_TIME,
  EMPTY_BOTTOM_ROWS,
  INVALID_VALUE,
  PROCESSING_ROWS_THRESHOLD,
  MINIMUM_ROWS,
  COL_CATEGORY,
  COL_FILTER_CRITERIA,
} from './helpers/constants';
import {
  addCategoryValidatorToRow,
  isEmptyRow,
  resumeAll,
  setValidations,
  suspendAll,
  validateRow,
  addFilterCriteriaValidatorToRow,
} from './helpers/validation';
import { clipboardClearOnCut } from '@helpers/clipboard-events';
import { DatasetsService } from '@services/datasets/datasets.service';
import { ToastLevel } from '@shared/toasterSetup';

@Component({
  selector: 'app-input-structure',
  templateUrl: './input-structure.component.html',
  styleUrls: ['./input-structure.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class InputStructureComponent implements OnInit, OnDestroy, AfterViewInit {
  get nrows(): number {
    return this.data.filter((r, index) => !isEmptyRow(index, this.data)).length;
  }

  get showProcessing(): boolean {
    return this.isProcessing && this.data.length > PROCESSING_ROWS_THRESHOLD;
  }

  get showLatest$(): Observable<boolean> {
    const showLatest =
      this.dataLoaded &&
      this.isEdit &&
      this.balanceSheetTimestampOnLoad &&
      this.tempBalanceSheetTimestamp &&
      this.balanceSheetTimestampOnLoad.getTime() !== this.tempBalanceSheetTimestamp.getTime();

    return of(showLatest);
  }

  get dataShow() {
    return JSON.stringify(this.data, null, 2).trim();
  }

  get dirty(): boolean {
    const val = this.sheet == null ? false : this.sheet.getDirtyCells(0, 0, this.sheet.getRowCount(), 7).length > 0;
    return val;
  }

  constructor(
    public balanceSheetService: BalanceSheetService,
    private auth: OpenIdConnectService,
    private zone: NgZone,
    private changeRef: ChangeDetectorRef,
    private menu: MenuService,
    private notifications: NotificationsService,
    private permissions: PermissionsService,
    private datasetsService: DatasetsService
  ) {}

  @ViewChild('spreadContainer', { static: false }) spreadContainer: ElementRef;
  @ViewChild('spreadDiv', { static: false }) spreadDiv;
  public spreadBackColor = spreadsheetConfig.backColor;
  public hostStyle = spreadsheetConfig.hostStyle;

  @Output() public notificationBanner$ = new EventEmitter();

  private timeout: NodeJS.Timer = null;
  private enterCellTimeout: NodeJS.Timer = null;
  private leaveCellTimeout: NodeJS.Timer = null;
  private addValidationTimeout: NodeJS.Timer = null;
  private validateAndStoreTimeout: NodeJS.Timer = null;
  private interval: NodeJS.Timer = null;

  public data: Array<BalanceSheet> = [];
  public savedDataJSON: string = null;

  private numberOfRows = 0;
  private balanceSheetTimestampOnLoad: Date = null;
  private tempBalanceSheetTimestamp: Date = null;
  private balanceSheetUserOnLoad: number = null;
  private balanceSheetTimestampCurrent: Date = null;
  private balanceSheetUserCurrent: number = null;
  private balanceSheetUserNameCurrent: string = null;
  private balanceSheetTimeDiff: number = null;
  public isEdit = false;
  public isBalanceSheetSaved = true;
  public unsavedChanges$ = new BehaviorSubject<boolean>(!this.isBalanceSheetSaved);
  public isLoading = true;
  private userRoleCanEdit;
  private destroyed$ = new Subject();

  /**
   * Application is busy adding validations or validating sheet
   */
  public isProcessing = true;
  public validSheet = false;
  public dataLoaded = false;
  public showLatest = false;
  private sheet: GC.Spread.Sheets.Worksheet = null;
  private spread: GC.Spread.Sheets.Workbook;
  public mouseEvent: any;
  public banner: { title: string; body: string; actions: object[] }; // = { title: '', body: '', actions: [] };
  private addingEmptyRows = false;
  private deletingEmptyRows = false;

  public categories: Array<BalanceSheetCategory>;
  public categoryNames: Array<string>;
  public categoryNamesString: string;
  private readonly _keys = Object.keys(new BalanceSheet()).sort();
  private readonly categoryMapping = {
    0: this._keys[3],
    1: this._keys[0],
    2: this._keys[6],
    3: this._keys[1],
    4: this._keys[7],
    5: this._keys[4],
    6: this._keys[5],
    7: this._keys[2],
  };
  private validationMessages = {};
  private total;

  private storedBalanceSheets: BalanceSheetDB[];

  @HostListener('window:resize', [])
  onResize() {
    this.setPageColumns();
  }

  ngOnInit(): void {
    this.userRoleCanEdit = this.permissions.getPermission('ModelStructureBalanceSheet') === 'W';
  }

  ngAfterViewInit() {
    this.getCategoriesBalanceSheetsAndUsers();

    this.onMouseMoveOverSpreadsheet();
    this.listenToDialogSaveButton();

    this.balanceSheetService.subscriptions.push(
      this.balanceSheetService.invalidRows$
        .pipe(takeUntil(this.destroyed$), skip(1), distinctUntilChanged(), throttleTime(250))
        .subscribe((invalidRows: number[]) => {
          this.validSheet = invalidRows.length === 0;
          this.checkSaveButtonIsEnabled(`invalidRows$ change`);
        }),

      // Global paste detection - intercept paste before it even reaches SpreadJS
      // This will provide another layer of protection against pasting too many columns
      fromEvent(document, 'paste')
        .pipe(takeUntil(this.destroyed$))
        .subscribe((event: ClipboardEvent) => {
          // Only handle paste events when the spreadsheet has focus
          if (this.sheet && this.spread && document.activeElement.closest(`#${spreadsheetConfig.containerParentId}`)) {
            try {
              const clipboardData = event.clipboardData;
              if (clipboardData && clipboardData.getData) {
                const text = clipboardData.getData('text');
                if (text) {
                  const lines = text.trim().split('\n');
                  if (lines.length > 0) {
                    // Calculate max columns in clipboard data
                    const columnCounts = lines.map(line => line.split('\t').length);
                    const maxColumns = getColumnNames(this.sheet).length;
                    const colCount = Math.max(...columnCounts);
                    const startCol = this.sheet.getActiveColumnIndex();
                    
                    // If paste would exceed available columns, prevent it
                    if (startCol + colCount > maxColumns) {
                      console.log(`[Document-level paste prevention] Blocked paste of ${colCount} columns at column ${startCol}`);
                      event.preventDefault();
                      event.stopPropagation();
                      
                      // Show notification to user
                      this.notifications.showToast(
                        ToastLevel.Warning, 
                        { message: 'The data you are trying to paste contains more columns than are available in the sheet.' } as Toast
                      );
                      return false;
                    }
                  }
                }
              }
            } catch (error) {
              console.error('Error in document paste handler:', error);
            }
          }
        }),

      // Enhanced clipboard paste detection - Use keydown for early detection, handle async with setTimeout
      fromEvent(document, 'keydown')
        .pipe(takeUntil(this.destroyed$))
        .subscribe((event: KeyboardEvent) => {
          const isPasteCommand = event && ['KeyV', 'v'].includes(event.code) && event.ctrlKey;
          if (isPasteCommand && this.sheet && document.activeElement.closest(`#${spreadsheetConfig.containerParentId}`)) {
            console.log('Paste detected via keyboard shortcut');
            try {
              // Immediately ensure the sheet has enough rows to handle the paste
              // We'll prepare for paste before the browser's default paste handler runs
              // The actual row count will be handled in the ClipboardPasting event later
              
              // Get current selection
              const selection = this.sheet.getSelections()[0];
              if (selection) {
                // For Ctrl+V, we need to ensure post-processing happens after the browser's paste operation
                // First, let the clipboard pasting event run (which will add rows if needed)
                // Then handle any additional processing after the paste is complete
                const range = {
                  row: selection.row >= 0 ? selection.row : 0,
                  col: selection.col >= 0 ? selection.col : 0,
                  rowCount: selection.rowCount,
                  colCount: selection.colCount,
                };
                
                // Use a slightly longer timeout to ensure the paste has fully completed
                setTimeout(() => {
                  this.clipboardPastedCallback(range);
                  this.isValidSheet();
                }, 50); // Allow time for the paste operation to complete
              } else {
                console.warn('Paste detected but no selection available');
              }
            } catch (error) {
              console.error('Error handling paste shortcut:', error);
            }
          }
        })
    );

    this.changeRef.detectChanges();
    if (this.spread) {
      this.spread.focus();
    }
    this.isProcessing = false;
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();

    this.balanceSheetService.subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.balanceSheetService.invalidRows = [];
    clearInterval(this.interval);
  }

  /**
   * Listens to a channel of events that emits on click events of the save button on the dialog.
   * If is triggered for this page, calls the saveData method
   */
  private listenToDialogSaveButton() {
    this.balanceSheetService.subscriptions.push(
      this.menu.dialogSaveButton$
        .pipe(
          // debounceTime(250),
          distinctUntilChanged()
        )
        .subscribe((val: DialogSaveObject) => {
          if (val.page === 'balance-sheet' && val.clicked && val.enabled) {
            this.saveData();
          }
        }),
      this.menu.deactivatePopupClosed$.subscribe((popupResult) => {
        if (popupResult === false) {
          this.spread.focus();
        }
      })
    );
  }

  /**
   * We send the most up-to-date data of the balance sheet to the service that propagates it to his consumers
   */
  notifyBSDataUpdate(newData: BalanceSheet[]) {
    this.balanceSheetService.data = newData;
  }

  private onMouseMoveOverSpreadsheet() {
    this.mouseEvent = fromEvent(this.spreadContainer.nativeElement, 'mousemove').subscribe((e: MouseEvent) =>
      // @ts-ignore:next-line
      this.onMouseMove(e.layerX, e.layerY)
    );
    this.balanceSheetService.subscriptions.push(this.mouseEvent);
  }

  private getCategoriesBalanceSheetsAndUsers() {
    this.isLoading = true;
    this.isProcessing = true;

    forkJoin(this.balanceSheetService.getCategories(), this.balanceSheetService.getBalanceSheets())
      .toPromise()
      .then((data) => {
        // Categories
        this.categories = clone(data[0] as BalanceSheetCategory[]);
        this.categoryNames = ['', ...this.categories.map((x) => x.name)];
        this.total = this.categories.filter((x) => x.name.match(/total/gi))[0].name;
        this.categoryNamesString = JSON.stringify(this.categoryNames).slice(1).slice(0, -1);

        // Balance Sheets
        this.onRetrievedBalanceSheet(data[1] as RetrievedBalanceSheet);

        setTimeout(() => this.isValidSheet());

        this.isLoading = false;
      });
  }

  private onRetrievedBalanceSheet(retrievedBalanceSheet: RetrievedBalanceSheet) {
    // const delay = this.data.length > 100 ? 250 : 100;
    const delay = 0;
    const isTempRecord = !!(retrievedBalanceSheet && retrievedBalanceSheet.isEdited);
    setTimeout(() => {
      // Error handling when switching tabs quickly
      try {
        this.loadSheet(retrievedBalanceSheet, isTempRecord);
        this.setIsEdit(isTempRecord);
        this.isBalanceSheetSaved = !isTempRecord;
        this.setShowLatest();
        this.setDataBinding();
        this.setBottomEmptyRows(`onRetrievedBalanceSheet`);
        this.sheet.setActiveCell(0, 0);
        setTimeout(() => this.applyCellStylings());
      } catch {}
    }, delay);
  }

  balanceSheetTimeCheck() {
    // Checks for the date of the stored Balance Sheet Structure
    // and the Editing Users (the ones with a record in the tracking table)
    this.balanceSheetService
      .getBalanceSheetsDate()
      .toPromise()
      .then((data: any) => {
        this.balanceSheetTimestampCurrent = new Date(data.date_time_created);

        if (!this.balanceSheetTimestampOnLoad) {
          this.balanceSheetTimestampOnLoad = this.balanceSheetTimestampCurrent;
        }

        this.balanceSheetUserCurrent = data.user_id_created;
        this.balanceSheetUserNameCurrent = data.user_name_created;
        this.balanceSheetTimeDiff =
          (this.balanceSheetTimestampCurrent.getTime() - this.balanceSheetTimestampOnLoad.getTime()) / 1000;

        const structureWasUpdated = this.balanceSheetTimeDiff > 0 && this.auth.userId !== this.balanceSheetUserCurrent;

        if (
          structureWasUpdated
          // && this.balanceSheetService.toastDismissed !== this.balanceSheetTimestampCurrent.getTime()
        ) {
          this.zone.run(() => (this.showLatest = true));
          this.notificationBanner$.emit('show');
          // this.balanceSheetService.toastDismissed = this.balanceSheetTimestampCurrent.getTime();
        }
      });

    // Editing Users Check
    this.editingUsersCheck();
  }

  /**
   * Updates this.balanceSheetService.editingUsers with the list of users currently editing the structure, excluding this user.
   */
  async editingUsersCheck() {
    this.balanceSheetService.getEditingUsers();
  }

  balanceSheetTimeCheckOnLoad() {
    const sheetDateSubscription = this.balanceSheetService
      .getBalanceSheetsDate()
      .toPromise()
      // .pipe(take(1))
      .then((data: any) => {
        this.balanceSheetTimestampOnLoad = getDateFromString(data.date_time_created);
        this.balanceSheetUserOnLoad = data.user_id_created;
        if (!this.isEdit) {
          this.showLatest = false;
        }
      });

    // Add the subscription to the array of subscriptions for being unsubscribed on destroy
    // this.balanceSheetService.subscriptions.push(sheetDateSubscription);
  }

  setShowLatest() {
    this.zone.run(() => {
      this.showLatest =
        this.userRoleCanEdit &&
        ((this.dataLoaded && this.isEdit) ||
          (this.isEdit &&
            this.balanceSheetTimestampOnLoad &&
            this.tempBalanceSheetTimestamp &&
            this.balanceSheetTimestampOnLoad.getTime() !== this.tempBalanceSheetTimestamp.getTime()));
    });
  }

  setIsEdit(val: boolean) {
    this.zone.run(() => {
      this.isEdit = val;
      if (this.unsavedChanges$.value !== val) {
        this.unsavedChanges$.next(val);
      }
      if (this.isEdit) {
        this.isBalanceSheetSaved = false;
      }
      this.setShowLatest();

      if (this.dataLoaded && !this.isLoading /* Note: they act differently */) {
        this.checkSaveButtonIsEnabled('setIsEdit');
      }
    });
  }

  loadSheet(balanceSheets: RetrievedBalanceSheet, isTempRecord: boolean = false) {
    // suspendAll(`loadSheet`);
    // Abort loading if tab changed
    if (!this.sheet || !this.spread) {
      return;
    }

    this.zone.run(() => {
      if (balanceSheets && balanceSheets.dataItems) {
        this.dataLoaded = true;
        this.balanceSheetService.invalidRows = [];
        this.storedBalanceSheets = balanceSheets.dataItems;
        this.setIsEdit(balanceSheets.isEdited);

        const nRows = this.storedBalanceSheets.length;

        try {
          // This line causes an error when switching tabs quickly, where 'this.sheet' still exists upon function call,
          // but stops existing through its execution.
          this.sheet.setRowCount(nRows || EMPTY_BOTTOM_ROWS);
        } catch {}

        if (this.storedBalanceSheets) {
          const dataCopy: BalanceSheet[] = [];
          this.storedBalanceSheets.forEach((item: BalanceSheetDB, index) => {
            const matchedCategory =
              this.categories && this.categories.find((c) => Number(c.id) === Number(item.category));

            dataCopy.push(<BalanceSheet>{
              AccountName: item.account_name === INVALID_VALUE || item.account_name === '' ? null : item.account_name,
              AccountNumber: item.account_number === INVALID_VALUE ? null : item.account_number,
              AltTotalFlag: item.alt_total_flag,
              Category: matchedCategory ? matchedCategory.name : isTempRecord ? item.category : null,
              DisplayOrder: item.display_order === INVALID_VALUE ? null : item.display_order,
              FilterCriteria:
                item.filter_criteria === null || item.filter_criteria === undefined ? '' : item.filter_criteria,
              Mnemonic: item.mnemonic === '' ? null : item.mnemonic,
              ParentAcctNum: item.parent_account_number === INVALID_VALUE ? null : item.parent_account_number,
            });
          });
          // Initial assignment to this.data
          this.data = dataCopy;

          // this.cleanFilterCriteria();

          this.savedDataJSON = JSON.stringify(dataCopy.filter((d, i) => !isEmptyRow(i, dataCopy)));
        }

        this.tempBalanceSheetTimestamp =
          this.storedBalanceSheets[0] && this.storedBalanceSheets[0].date_time_created
            ? new Date(this.storedBalanceSheets[0].date_time_created)
            : new Date();

        this.balanceSheetTimeCheckOnLoad();
        this.startCheckTimer();
      } else {
        this.dataLoaded = false;
        this.setIsEdit(false);
      }

      this.notifyBSDataUpdate(this.data);
    });
  }

  startCheckTimer() {
    this.stopCheckTimer();
    this.interval = setInterval(() => {
      this.balanceSheetTimeCheck();
    }, BALANCE_SHEET_CHECK_RATE);
  }

  stopCheckTimer() {
    clearInterval(this.interval);
    this.interval = null;
  }

  async loadLatest() {
    // Get Latest
    this.isLoading = true;
    this.isProcessing = true;

    const retrievedBalanceSheet = <RetrievedBalanceSheet>await this.balanceSheetService.loadLatest();

    if (retrievedBalanceSheet) {
      this.onRetrievedBalanceSheet(retrievedBalanceSheet);
      this.notificationBanner$.emit('dismiss');
      this.isBalanceSheetSaved = true;
      this.setShowLatest();
      this.editingUsersCheck();

      setTimeout(() => {
        this.isLoading = false;
        this.isProcessing = false;
      }, 250);
    } else {
      throw new Error('Unable to retrieve the latest sheet data or error deleting the tracking record');
    }
  }

  workbookInit(args) {
    this.spread = args.spread;
    this.setupWorkBook();
  }

  private setupWorkBook() {
    this.sheet = this.spread.getActiveSheet();

    // =========================
    //   General Sheet Options
    // =========================
    this.sheet.name('Balance Sheet Structure');
    this.sheet.setColumnCount(7);
    this.sheet.defaults.rowHeight = 20;
    this.sheet.defaults.colWidth = 146;

    setDefaultBookOptions(this.spread.options, { allowExtendPasteRange: true });
    setDefaultSheetOptions(this.sheet.options);

    this.spread.options.defaultDragFillType = 3; // GC.Spread.Sheets.Fill.fillWithoutFormatting;
    this.spread.options.showHorizontalScrollbar = false;
    this.spread.options.allowExtendPasteRange = true;

    if (!this.userRoleCanEdit) {
      this.sheet.getRange(-1, 0, -1, this.sheet.getColumnCount()).locked(true);
      this.sheet.options.isProtected = true;
      this.sheet.options.protectionOptions.allowSelectLockedCells = true;
      SpreadContextMenu.setCopyCutPaste(this.spread);
    } else {
      // Context Menu
      SpreadContextMenu.setCopyCutPaste(this.spread)
        .enableInsertRowsAboveAndBelow(this.spread)
        .enableDeleteRows(this.spread);
    }

    this.setActionKeysEvents();
    this.setSheetEventBindings();
  }

  private setActionKeysEvents() {
    const actionKeysCallback = () => {
      const row = this.sheet.getActiveRowIndex();
      const col = this.sheet.getActiveColumnIndex();

      if (col === COL_CATEGORY) {
        const suspendState = suspendAll(this.sheet, `deleteKey`);

        setTimeout(() => this.applyCellStylings(row));

        // Check if new value is also valid for the 'other' category validator (formula)
        addCategoryValidatorToRow(this, row);
        addFilterCriteriaValidatorToRow(this.sheet, row, this.categories);
        resumeAll(this.sheet, `deleteKey`, suspendState);
      }

      this.validateAndStore();
      this.checkIfDataChanged();

      // this.checkSaveButtonIsEnabled('actionKeysCallback');
      // setTimeout(() => this.checkBottomEmptyRows());
    };

    // Below line was commented since event was being triggered twice, as part of Task 3338
    mapActionKeys(this.spread, this.sheet, actionKeysCallback, this.userRoleCanEdit);

    // Undo & Redo
    // this.mapActionsOnUndoRedoOrDelete(actionKeysCallback);
  }

  /**
   * If Structure is different to the latest loaded / saved one, set IsEdit to true
   */
  private checkIfDataChanged() {
    const dataJSON = JSON.stringify(this.data.filter((d) => d.AccountName));
    this.setIsEdit(this.isEdit || dataJSON !== this.savedDataJSON);
  }

  private setPageColumns() {
    const rowWidths = [160, 260, 150, 150, 150, 110];
    const width = this.spreadContainer.nativeElement.clientWidth;
    let filterCriteriaWidth =
      width - 62 - (rowWidths[0] + rowWidths[1] + rowWidths[2] + rowWidths[3] + rowWidths[4] + rowWidths[5]);

    // Set a min-width for filter criteria width
    // filterCriteriaWidth = filterCriteriaWidth > 120 ? filterCriteriaWidth : 120;

    if (filterCriteriaWidth > 120) {
      this.spread.options.showHorizontalScrollbar = false;
    } else {
      filterCriteriaWidth = 120;
      this.spread.options.showHorizontalScrollbar = true;
    }

    if (this.sheet) {
      this.sheet.bindColumns([
        { name: 'Category', displayName: 'Category', size: rowWidths[0] },
        {
          name: 'AccountName',
          displayName: 'Account Name',
          size: rowWidths[1],
        },
        { name: 'Mnemonic', displayName: 'Mnemonic', size: rowWidths[2] },
        {
          name: 'AccountNumber',
          displayName: 'Account #',
          size: rowWidths[3],
        },
        {
          name: 'ParentAcctNum',
          displayName: 'Parent #',
          size: rowWidths[4],
        },
        {
          name: 'DisplayOrder',
          displayName: 'Display Order',
          size: rowWidths[5],
        },
        {
          name: 'FilterCriteria',
          displayName: 'Filter Criteria',
          size: filterCriteriaWidth,
        },
      ]);

      this.sheet.setFormatter(-1, 3, '#', GC.Spread.Sheets.SheetArea.viewport);
      this.sheet.setFormatter(-1, 4, '#', GC.Spread.Sheets.SheetArea.viewport);
    }
  }

  setDataBinding() {
    this.sheet.setDataSource(this.data);
    this.setPageColumns();
  }

  private mapActionsOnUndoRedoOrDelete(actionsToPerform) {
    // ----------------------------------------------------------------------------------
    // Delete Key
    const deleteCommand = {
      canUndo: true,
      execute: function (context, options, isUndo) {
        const Commands = GC.Spread.Sheets.Commands;
        if (isUndo) {
          // Undo feature for 'Delete' key only
          Commands.undoTransaction(context, options);
          return true;
        } else {
          Commands.startTransaction(context, options);
          // Actions on Delete
          actionsToPerform();
          Commands.endTransaction(context, options);
          return false;
        }
      },
    };

    const commandManager = this.spread.commandManager();
    if (this.userRoleCanEdit) {
      commandManager.register('deleteKey', deleteCommand, GC.Spread.Commands.Key.del);
    }

    // ----------------------------------------------------------------------------------
    // UNDO: Ctrl + Z
    const undoCommand = () => {
      // If allowUndo is true, undo:
      if (this.spread.undoManager().canUndo()) {
        // Actions when undo
        actionsToPerform();
      }
    };
    this.spread.commandManager().register('undo', undoCommand, GC.Spread.Commands.Key.z, true, false, false, false);

    // ----------------------------------------------------------------------------------
    // REDO: Ctrl + Y
    const redoCommand = () => {
      // If allowUndo is true, redo:
      if (this.spread.undoManager().canRedo()) {
        actionsToPerform();
      }
    };
    this.spread.commandManager().register('redo', redoCommand, GC.Spread.Commands.Key.y, true, true, false, false);
  }

  setSheetEventBindings() {
    bindSpreadJSEventsToSheet(this.sheet);

    // RowChanged (Event when a row is inserted or deleted - on insert )
    this.sheet.bind(GC.Spread.Sheets.Events.RowChanged, (e, info) => {
      const suspendState = suspendAll(this.sheet);

      this.cleanFilterCriteria();

      if (
        !this.addingEmptyRows &&
        !this.deletingEmptyRows &&
        (info.propertyName === 'addRows' || info.propertyName === 'deleteRows')
      ) {
        if (info.propertyName === 'addRows') {
          for (let row = info.row; row < info.row + info.count; row++) {
            this.applyCellStylings(row);
          }
        }

        this.validateAndStore();
        this.setIsEdit(true);
        this.setBottomEmptyRows(`RowChanged`);
      }

      this.numberOfRows = this.sheet.getRowCount();
      if (this.addValidationTimeout) {
        clearTimeout(this.addValidationTimeout);
        this.addValidationTimeout = null;
      }
      this.addValidationTimeout = setTimeout(() => {
        setValidations(this);
      }, 250);

      resumeAll(this.sheet, `rowChanged`, suspendState);
    });

    this.sheet.bind(GC.Spread.Sheets.Events.CellClick, this.selectAllCallback);
    this.sheet.bind(GC.Spread.Sheets.Events.CellDoubleClick, this.selectAllCallback);

    this.sheet.bind(GC.Spread.Sheets.Events.ClipboardPasting, (e, info) => {
      console.log('ClipboardPasting event triggered', info);
      
      try {
        // If this is a cut event, clear clipboard content once event ends
        clipboardClearOnCut(info, this.sheet);
        
        // Validate paste data before proceeding
        if (!info) {
          console.warn('ClipboardPasting event received null info');
          return;
        }

        // Calculate rows needed from clipboard data
        let rowCount = 1;
        if (info.pasteData && info.pasteData.text) {
          const lines = info.pasteData.text.trim().split('\n');
          rowCount = lines.length;
          console.log(`Clipboard contains ${rowCount} rows of data`);
        } else if (info.cellRange && info.cellRange.rowCount) {
          rowCount = info.cellRange.rowCount;
        }

        // Check if we're trying to paste a large amount of data
        if (rowCount > PROCESSING_ROWS_THRESHOLD) {
          this.isProcessing = true;
          console.log(`Processing large paste operation: ${rowCount} rows`);
        }
        
        // Prevent pasting if it would exceed the available columns
        // This must be called before allowing row expansion
        if (SheetFunctions.preventPastingColumnsBeyond(this.sheet, e, info)) {
          console.log('Paste operation prevented - exceeds available columns');
          
          // Stop the default paste behavior using a stronger approach
          e.preventDefault();
          
          // Force SpreadJS to stop the paste operation
          if (typeof e.stopPropagation === 'function') e.stopPropagation();
          if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
          
          // Show notification to user
          this.notifications.showToast(
            ToastLevel.Warning, 
            { message: 'The data you are trying to paste contains more columns than are available in the sheet.' } as Toast
          );
          
          // Cancel any further processing
          return;
        }

        // Allow pasting data beyond the current row count by automatically adding rows
        // This should handle adding the correct number of rows regardless of clipboard content size
        SheetFunctions.allowPastingRowsBeyond(this.sheet, info);
        
      } catch (error) {
        console.error('Error handling clipboard pasting event:', error);
        // Don't prevent default paste behavior even if our handler fails
      }
    });

    this.sheet.bind(GC.Spread.Sheets.Events.ClipboardPasted, (e, info) => {
      try {
        console.log('ClipboardPasted event triggered', info);
        if (info && info.cellRange) {
          this.clipboardPastedCallback(info.cellRange);
        } else {
          console.warn('ClipboardPasted event received without valid cell range');
        }
      } catch (error) {
        console.error('Error in ClipboardPasted event handler:', error);
        // Attempt recovery by checking for active selection
        try {
          const selection = this.sheet.getSelections()[0];
          if (selection) {
            this.clipboardPastedCallback({
              row: selection.row >= 0 ? selection.row : 0,
              col: selection.col >= 0 ? selection.col : 0,
              rowCount: selection.rowCount,
              colCount: selection.colCount
            });
          }
        } catch (recoveryError) {
          console.error('Failed to recover from ClipboardPasted error:', recoveryError);
        }
      }
    });

    // DragFillBlockCompleted
    this.sheet.bind(GC.Spread.Sheets.Events.DragFillBlockCompleted, (e, info) => {
      this.clipboardPastedCallback(info.fillRange);
    });

    // ValueChanged - Event when user edits a cell (double-click, edit, enter)
    this.sheet.bind(GC.Spread.Sheets.Events.ValueChanged, (e, info) => {
      this.valueChangedCallback(info);
    });
  }

  private clipboardPastedCallback(range) {
    console.log('clipboardPastedCallback called with range:', range);
    if (!range) {
      console.warn('No range provided to clipboardPastedCallback');
      return;
    }

    if (this.userRoleCanEdit) {
      try {
        // Validate range parameters
        if (range.row < 0) range.row = 0;
        if (range.col < 0) range.col = 0;
        if (!range.rowCount || range.rowCount < 1) range.rowCount = 1;
        if (!range.colCount || range.colCount < 1) range.colCount = 1;
        
        console.log(`Processing paste in range: Row ${range.row}, Col ${range.col}, RowCount ${range.rowCount}, ColCount ${range.colCount}`);
        
        // Check if we need to add more rows to accommodate paste
        const currentRowCount = this.sheet.getRowCount();
        const requiredRowCount = range.row + range.rowCount;
        
        if (requiredRowCount > currentRowCount) {
          console.log(`Adding ${requiredRowCount - currentRowCount} additional rows to accommodate pasted data`);
          this.sheet.addRows(currentRowCount, requiredRowCount - currentRowCount);
        }
        
        // Update data in Angular zone to ensure change detection
        this.zone.run(() => {
          this.notifyBSDataUpdate(this.data);
        });

        // Special handling for Category column (col 0)
        if (range.col === 0) {
          const suspendState = suspendAll(this.sheet, `clipboardPastedCallback`);
          try {
            for (let r = range.row; r < range.row + range.rowCount; r++) {
              if (r < this.sheet.getRowCount()) {
                addCategoryValidatorToRow(this, r);
                addFilterCriteriaValidatorToRow(this.sheet, r, this.categories);
              }
            }
          } catch (error) {
            console.error('Error applying validators to pasted cells:', error);
          } finally {
            resumeAll(this.sheet, `clipboardPastedCallback`, suspendState);
          }
        }

        // For large paste operations, we may need separate handling
        const isLargePaste = range.rowCount > PROCESSING_ROWS_THRESHOLD;
        if (isLargePaste) {
          console.log(`Processing large paste operation with ${range.rowCount} rows`);
          // For large pastes, we'll process in smaller batches or use a longer timeout
        }

        // Schedule post-paste processing to run after the paste operation completes
        setTimeout(() => {
          try {
            this.cleanCategoryValues(); // Clean up whitespace-only category values
            this.applyCellStylings();
            this.checkIfDataChanged();
            this.validateAndStore();
            
            // Ensure we have enough empty rows at the bottom after pasting
            this.setBottomEmptyRows(`clipboardPastedCallback`);
            
            // Make sure we can see the pasted data by scrolling to it if needed
            if (range.rowCount > 1) {
              // Scroll to make the last row of pasted data visible
              const lastPastedRow = range.row + range.rowCount - 1;
              this.sheet.showRow(lastPastedRow, GC.Spread.Sheets.VerticalPosition.center);
            }
          } catch (error) {
            console.error('Error in post-paste processing:', error);
          } finally {
            // Always reset processing flag after paste operations complete
            this.isProcessing = false;
          }
        }, isLargePaste ? 300 : 100); // Use longer delay for large pastes
      } catch (error) {
        console.error('Error in clipboardPastedCallback:', error);
        this.isProcessing = false;
      }
    }
  }

  private selectAllCallback = (event, info) => {
    if (info.sheetArea === GC.Spread.Sheets.SheetArea.corner) {
      this.sheet.setSelection(0, 0, this.sheet.getRowCount(), this.sheet.getColumnCount());
    }
  };

  private valueChangedCallback(info) {
    const suspendState = suspendAll(this.sheet, `EditEnded`);
    const { row, col, newValue } = info;
    const itemProp: string = this.categoryMapping[col];

    this.data[row][itemProp] = isNumber(newValue) ? Number(newValue) : newValue;

    // Category
    if (col === COL_CATEGORY) {
      // Handle whitespace-only category values by converting them to null
      if (this.data[row][itemProp] === 0 || 
          (typeof this.data[row][itemProp] === 'string' && this.data[row][itemProp].trim() === '')) {
        this.data[row][itemProp] = null;
      }

      checkDropdownValues(this.sheet, info.row, info.col, info.editingText, null, this.categoryNames);
      this.applyCellStylings(info.row);

      // Check if new value is also valid for the 'other' category validator (formula)
      addCategoryValidatorToRow(this, info.row);
      addFilterCriteriaValidatorToRow(this.sheet, info.row, this.categories);
    }

    // Display Order
    if (col === COL_DISPLAY_ORDER) {
      if (this.data[row][itemProp] === '') {
        this.data[row][itemProp] = 0;
      }
    }

    // Filter Criteria
    if (col === COL_FILTER_CRITERIA) {
      addFilterCriteriaValidatorToRow(this.sheet, info.row, this.categories);
    }

    this.zone.run(() => {
      // Notify to the consumers
      this.notifyBSDataUpdate(this.data);
    });

    this.checkIfDataChanged();

    this.validateAndStore();
    this.setBottomEmptyRows(`valueChangedCallback`);

    resumeAll(this.sheet, `EditEnded`, suspendState);
  }

  private applyCellStylings(rowNumber: number = null) {
    // Error handling when quickly switching tabs

    try {
      const suspendState = suspendAll(this.sheet, `applyCellStylings`);

      if (rowNumber === null) {
        this.data.forEach((row: BalanceSheet, index) => {
          const rowRange = this.sheet.getRange(index, 0, 1, 7);
          if (isEmpty(row.Category)) {
            setNonCategoryStyle(rowRange);
          } else {
            setCategoryStyle(rowRange);
          }
        });
      } else {
        const rowRange = this.sheet.getRange(rowNumber, 0, 1, 7);
        const row = this.data[rowNumber];

        if (isEmpty(row.Category)) {
          setNonCategoryStyle(rowRange);
        } else {
          setCategoryStyle(rowRange);
        }
      }
      resumeAll(this.sheet, 'applyCellStylings', suspendState);
    } catch {}
  }

  validateAndStore() {
    if (this.userRoleCanEdit) {
      if (!this.isLoading) {
        if (this.validateAndStoreTimeout) {
          clearTimeout(this.validateAndStoreTimeout);
          this.validateAndStoreTimeout = null;
        }

        this.validateAndStoreTimeout = setTimeout(() => {
          this.cleanFilterCriteria();
          this.cleanCategoryValues(); // Clean up whitespace-only category values

          if (this.isEdit && !this.isBalanceSheetSaved) {
            this.isValidSheet();
            this.notifyBSDataUpdate(this.data);
            this.storeTemp();
          }
        }, DEBOUNCE_TIME);
      }
    }
  }

  storeTemp() {
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
    this.timeout = setTimeout(() => {
      const serializedData = this.serializeData(true);
      const trackingItem: UserEditTrackingItem = {
        user_id: this.auth.userId,
        date_time_created: '',
        table_data: serializedData,
        edited_table: 'Balance_Sheet_Item',
      };

      this.balanceSheetService
        .temporaryStoreBalanceSheet(trackingItem)
        .toPromise()
        .then((result: string) => {
          // If the user has save, we avoid to save the tracking record
          this.setIsEdit(!this.isBalanceSheetSaved);
          setTimeout(() => {
            this.isProcessing = false;
          }, 250);
        });
    }, 0);
  }

  // After the user clicks the "save & close" button - save the data with a permanent flag
  private async saveData() {
    const serializedData: string = this.serializeData();

    const storedBalanceSheet: any = await this.balanceSheetService.permanentlyStoreBalanceSheet(serializedData);

    if (storedBalanceSheet.Success) {
      const newData: BalanceSheetDB[] = clone(storedBalanceSheet.StoredData);
      this.zone.run(() => {
        this.isBalanceSheetSaved = true;
        this.dataLoaded = true;

        if (newData && newData[0] && newData[0].date_time_created) {
          this.tempBalanceSheetTimestamp = new Date(newData[0].date_time_created);
          this.balanceSheetTimestampOnLoad = new Date(newData[0].date_time_created);
        }
        this.notifyBSDataUpdate(this.data);
      });

      this.savedDataJSON = JSON.stringify(this.data.filter((d) => d.AccountName));

      this.setIsEdit(false);

      // Show save confirmation toast message
      // ------------------------------------
      await this.datasetsService.getRequiredDatasetImport();
      if (this.datasetsService.isImportRequired) {
        // if (storedBalanceSheet.Import_Required) {
        this.notifications.showToast(ToastLevel.Success,
          { message: 'Balance Sheet successfully updated! Import or aggregation is required!' } as Toast,
          'GO TO IMPORT PAGE',
          'sim-importer'
        );
      } else {
        this.notifications.showToast(ToastLevel.Success, {message: 'Balance Sheet successfully updated!' } as Toast);
      }
      this.loadLatest();
    } else {
      this.dataLoaded = true;
      this.isBalanceSheetSaved = false;

      // Show error message
      this.notifications.showToast(ToastLevel.Error, 
        { message: 'Error saving in the database. Loading latest available data',
      } as Toast);
    }
  }

  // Shortening property names and filtering null values out to reduce string length
  serializeData(isTempRecord = false) {
    const prepareData = (account: BalanceSheet): BalanceSheetDB => {
      if (this.categories) {
        const matchedCategory = this.categories.find(
          (c) => c.name && account['Category'] && c.name.toLocaleLowerCase() === account['Category'].toLocaleLowerCase()
        );
        const result: BalanceSheetDB = {
          category: matchedCategory
            ? matchedCategory.id.toString()
            : isTempRecord
            ? account['Category'] // Leave it as it is for temp records
            : null, // It is saving data and It has no category found? Set it to null
          account_name: account['AccountName'] !== null ? account['AccountName'] : '',
          mnemonic: account['Mnemonic'] !== null ? account['Mnemonic'] : '',
          account_number:
            account['AccountNumber'] === null || isNaN(account['AccountNumber'])
              ? INVALID_VALUE
              : account['AccountNumber'],
          parent_account_number:
            account['ParentAcctNum'] === null || isNaN(account['ParentAcctNum'])
              ? INVALID_VALUE
              : account['ParentAcctNum'],
          display_order:
            account['DisplayOrder'] === null || isNaN(account['DisplayOrder']) ? 0 : account['DisplayOrder'],
          filter_criteria:
            account['FilterCriteria'] === null || account['FilterCriteria'] === undefined
              ? ''
              : account['FilterCriteria'],
          alt_total_flag: account['AltTotalFlag'] !== null ? account['AltTotalFlag'] : 0,
          date_time_created: null,
        };

        return result;
      }
      return null;
    };

    const dataCopy = clone(this.data).filter((account, index) => !isEmptyRow(index, this.data));
    const processedData = JSON.stringify(dataCopy.map((account) => prepareData(account))).replace(/\'/g, "''");

    return processedData;
  }

  removeHeaders() {
    const row: BalanceSheet = this.data[0];
    if (this.isHeaderRow(row)) {
      // this.data = clone(this.data).slice(1);
      console.warn(`Removed header`, { row });
      this.data.splice(0, 1);
    }
  }

  private cleanFilterCriteria() {
    // Making sure there are no NULL filter criteria
    this.data
      .filter((account, index) => !isEmptyRow(index, this.data))
      .filter((d) => d.FilterCriteria === null || d.FilterCriteria === undefined)
      .forEach((d) => (d.FilterCriteria = ''));
  }

  private cleanCategoryValues() {
    // Convert whitespace-only category values to null
    this.data.forEach((account, index) => {
      if (account.Category && typeof account.Category === 'string' && account.Category.trim() === '') {
        account.Category = null;
      }
    });
  }

  private isHeaderRow(row: any) {
    return (
      // Check for nulls
      row &&
      row.Category &&
      row.AccountName &&
      row.Mnemonic &&
      // Confirm header row
      row.Category.toLowerCase() === 'category' &&
      row.AccountName.toLowerCase() === 'name' &&
      row.Mnemonic.toLowerCase() === 'mnemonic'
    );
  }

  isValidSheet() {
    suspendAll(this.sheet, `isValidSheet`);
    this.sheet.suspendEvent();

    this.zone.run(() => {
      this.balanceSheetService.validatingRows = true;
      this.balanceSheetService.invalidRows = [];

      // Each invalid row index will be pushed into 'invalidRows' array

      for (const index in this.data) {
        if (this.data[index] && isNumber(index)) {
          setTimeout(() => {
            const idx = Number(index);
            if (idx === this.data.length - 1) {
              this.balanceSheetService.validatingRows = false;
              setTimeout(() => {
                this.checkSaveButtonIsEnabled(`isValidSheet`);
              });
            }
            validateRow(this.sheet, this.data, this.zone, this.balanceSheetService, idx);
          });
        }
      }
    });

    this.calculateTotalFlags();
    this.setShowLatest();

    this.sheet.resumeEvent();
    resumeAll(this.sheet, `isValidSheet`);

    return this.validSheet;
  }

  /**
   * This will iterate over this.data and set AltTotalFlag to 1 if account has children, or 0 if it doesn't
   */
  calculateTotalFlags() {
    const parents = [];

    this.data.forEach((account) => {
      if (parents.indexOf(account['ParentAcctNum']) === -1) {
        parents.push(account['ParentAcctNum']);
      }
    });

    this.data.forEach(
      (account) => (account['AltTotalFlag'] = parents.indexOf(account['AccountNumber']) === -1 ? 0 : 1)
    );
  }

  /**
   * Remove bottom empty rows, keeping only `EMPTY_BOTTOM_ROWS` bottom rows
   */
  private setBottomEmptyRows(source: string = null) {
    this.addBottomEmptyRows(EMPTY_BOTTOM_ROWS);
  }

  private addBottomEmptyRows(rows: number) {
    this.addingEmptyRows = true;

    // Save current status
    const prevStatus = {
      // Double ! made intentionally to force the value to be true or false
      saved: !!this.isBalanceSheetSaved,
      edited: !!this.isEdit,
    };

    const numberOfRows = this.data.filter((d, i) => !isEmptyRow(i, this.data)).length;

    if (this.sheet.getRowCount() < MINIMUM_ROWS + EMPTY_BOTTOM_ROWS) {
      this.sheet.addRows(this.sheet.getRowCount(), MINIMUM_ROWS + EMPTY_BOTTOM_ROWS - numberOfRows);
    } else {
      let paddingRows = 0;
      if (this.sheet.getRowCount() > 1 && !isEmptyRow(this.sheet.getRowCount() - 1, this.data)) {
        ++paddingRows;
      }
      if (this.sheet.getRowCount() > 2 && !isEmptyRow(this.sheet.getRowCount() - 2, this.data)) {
        ++paddingRows;
      }
      this.sheet.addRows(this.sheet.getRowCount(), paddingRows);
    }

    // Maintain the status
    this.isBalanceSheetSaved = prevStatus.saved;
    this.setIsEdit(prevStatus.edited);
    this.setShowLatest();
    this.addingEmptyRows = false;
  }

  selectRow = (row) => {
    selectRow(row, this.sheet);
  };

  onMouseMove(layerX, layerY) {
    const x = layerX;
    const y = layerY;

    const target = this.sheet.hitTest(x, y);

    if (target) {
      const { row, col } = target;
      const thisCell = this.sheet.getCell(row, col);
      const categoryCell = this.sheet.getCell(row, COL_CATEGORY);
      const value = thisCell.value();
      const validator = thisCell.validator();
      const isValid = this.sheet.isValid(row, col, value);
      let nmdCategory, auxAssetCategory, auxLiabilityCategory;

      if (this.categories) {
        nmdCategory = this.categories.find((cat) => cat.id === NON_MATURITY_DEPOSIT_INDEX);
        auxAssetCategory = this.categories.find((cat) => cat.id === AUXILIARY_ASSET_INDEX);
        auxLiabilityCategory = this.categories.find((cat) => cat.id === AUXILIARY_LIABILITY_INDEX);
      }

      const validationMessages = [
        {
          field: 'Category',
          message: `Category must be one of\n${this.categoryNamesString}`,
        },
        {
          field: 'Account Name',
          message: value ? `The Account Name should be unique` : `Account Name can't be empty`,
        },
        {
          field: 'Mnemonic',
          message: value
            ? `The Mnemonic should be unique and contain valid Alphanumeric characters and underscores only`
            : `Mnemonic can't be empty`,
        },
        {
          field: 'Account Number',
          message: value
            ? `The Account Number should be an unique positive number (max. 18 digits)\nor have a Filter Criteria`
            : `Account Number can't be empty`,
        },
        {
          field: 'Parent Account #',
          message: value
            ? `This account's Parent# must refer to a non-auxiliary account in this existing structure unless this is a root account.`
            : `Parent Account # can't be empty`,
        },
        { field: 'Display Order', message: `Must be blank or a number >= 0` },
      ];

      // Change Filter Criteria validation message based on which category the account is
      if (categoryCell && categoryCell.value()) {
        switch (categoryCell.value()) {
          case auxAssetCategory.name:
          case auxLiabilityCategory.name:
            validationMessages.push({
              field: 'Filter Criteria',
              message: `Auxiliary accounts cannot have filter criteria.`,
            });
            break;
        }
      } else {
        validationMessages.push({
          field: 'Filter Criteria',
          message: `User Weight filter criteria can only be applied to ${
            nmdCategory ? nmdCategory.name : 'Non Maturity Deposits'
          } accounts or children accounts of ${nmdCategory ? nmdCategory.name : 'Non Maturity Deposits'}.`,
        });
      }

      const cell = validationMessages[col];

      if (validator && cell && !isValid) {
        if (col === COL_CATEGORY) {
          // Category
          if (this.validationMessages[`${row + 1}-0`]) {
            this.balanceSheetService.hoverNotification = {
              title: `(Row ${row + 1}) Invalid Category`,
              value,
              body: `${this.validationMessages[`${row + 1}-0`].join('\n')}`,
              type: 'error',
            };
          }
        } else {
          this.balanceSheetService.hoverNotification = {
            title: `Invalid ${cell.field}`,
            value,
            body: `${cell.message}`,
            type: 'error',
          };
        }
      } else {
        this.balanceSheetService.hoverNotification = {};
      }
    }
  }

  public showNewStructureBanner() {
    this.banner = {
      title: `There's a newer Balance Sheet Structure available (created by ${this.balanceSheetUserNameCurrent})`,
      body: `* This will replace your current Structure data`,
      actions: [
        {
          // Dismiss
          name: 'dismiss',
          wording: 'Dismiss',
          primary: false,
          clickHandler: ($event) => () => {
            // To avoid having multiple requests for dismiss, we force the current timestamp to be the one on load
            this.balanceSheetTimestampOnLoad = this.balanceSheetTimestampCurrent;
          },
        },
        {
          // Load Latest
          name: 'replace',
          wording: 'Load',
          primary: true,
          clickHandler: ($event) => this.loadLatest(),
        },
      ],
    };
  }

  private checkSaveButtonIsEnabled(src = null) {
    if (this.balanceSheetService.validatingRows) {
      return;
    }

    const enabled =
      this.balanceSheetService.invalidRows.length === 0 &&
      !this.balanceSheetService.validatingRows &&
      this.dataLoaded &&
      !this.addingEmptyRows &&
      this.isEdit &&
      !this.isBalanceSheetSaved &&
      this.userRoleCanEdit;

    if (this.menu.dialogSaveButton.enabled !== enabled) {
      // Enable/Disable save button
      this.menu.dialogSaveButton = {
        enabled,
        valid: this.validSheet,
        page: 'balance-sheet',
        clicked: false,
        source: src ? src : 'checkSaveButtonIsEnabled()',
      };
    }
  }
}
